import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import CartList from './CartList';
import BlendManager from './BlendManager';
import LoadingSpinner from '../LoadingSpinner';
import './BatchManager.css';

function BatchManager() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('active');
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  const [editingBatch, setEditingBatch] = useState(null);
  const [activeTab, setActiveTab] = useState({});
  const [woSearchTerm, setWoSearchTerm] = useState('');
  const [reordering, setReordering] = useState(false);
  const [components, setComponents] = useState({});
  const [loadingComponents, setLoadingComponents] = useState({});
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [editingBatchQty, setEditingBatchQty] = useState(null); // batchId being edited
  const [qtyEdits, setQtyEdits] = useState({}); // { bwo_id: newQty }
  const [savingQty, setSavingQty] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    fetchBatches();
  }, [filterStatus]);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchBatches();
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [woSearchTerm]);

  const fetchBatches = async (forceRefresh = false) => {
    setLoading(true);
    try {
      // Force refresh work order data from NetSuite if requested
      if (forceRefresh) {
        try {
          await axios.post('/api/workorders/refresh');
        } catch (refreshError) {
          console.error('Error refreshing work orders:', refreshError);
          // Continue even if refresh fails
        }
      }

      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (woSearchTerm.trim()) params.work_order_search = woSearchTerm.trim();

      const response = await axios.get('/api/batches', { params });
      setBatches(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching batches:', err);
      setError('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const toggleBatchExpansion = (batchId) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(batchId)) {
      newExpanded.delete(batchId);
    } else {
      newExpanded.add(batchId);
    }
    setExpandedBatches(newExpanded);
  };

  const handleStatusChange = async (batchId, newStatus) => {
    try {
      await axios.patch(`/api/batches/${batchId}/status`, { status: newStatus });
      await fetchBatches();
    } catch (err) {
      console.error('Error updating batch status:', err);
      alert('Failed to update batch status');
    }
  };

  const handleDeleteBatch = async (batchId, batchName, status, event) => {
    // Stop event propagation to prevent expanding/collapsing
    if (event) {
      event.stopPropagation();
    }

    let confirmMessage = `Are you sure you want to delete batch ${batchName}?`;
    if (status === 'in_production') {
      confirmMessage += '\n\nWARNING: This batch is currently in production!';
    } else if (status === 'completed') {
      confirmMessage += '\n\nWARNING: This batch has been completed!';
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.delete(`/api/batches/${batchId}`);
      await fetchBatches();
    } catch (err) {
      console.error('Error deleting batch:', err);
      alert(err.response?.data?.error || 'Failed to delete batch');
    }
  };

  const exportBatchToCSV = (batch) => {
    let csv = `Batch: ${batch.batch_name}\n`;
    csv += `Fragrance: ${batch.fragrance_oil_name}\n`;
    csv += `Status: ${batch.status}\n`;
    csv += `Total Units: ${batch.total_units}\n`;
    csv += `Oil Required: ${batch.total_oil_lbs} lbs\n`;
    csv += `Wax Required: ${batch.total_wax_lbs} lbs\n`;

    csv += 'Work Order,Customer,Item,Original Qty,Batch Qty,Remaining Qty,Units,Oil (lbs),Wax (lbs)\n';

    batch.batch_work_orders.forEach(wo => {
      const units = wo.batch_quantity * (wo.case_qty || 1);
      const remaining = wo.quantity_remaining !== undefined ? wo.quantity_remaining : (wo.original_quantity - wo.batch_quantity);
      csv += `"${wo.work_order_number}","${wo.customer_name}","${wo.item_description}",`;
      csv += `${wo.original_quantity},${wo.batch_quantity},${remaining},${units},`;
      csv += `${wo.oil_needed_lbs},${wo.wax_needed_lbs}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-${batch.batch_name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return '#999';
      case 'in_production':
        return '#ff9800';
      case 'completed':
        return '#4caf50';
      default:
        return '#666';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch components for a batch
  const fetchComponents = async (batchId) => {
    if (components[batchId]) {
      return; // Already loaded
    }

    setLoadingComponents(prev => ({ ...prev, [batchId]: true }));
    try {
      const response = await axios.get(`/api/batches/${batchId}/components`);
      setComponents(prev => ({ ...prev, [batchId]: response.data }));
    } catch (err) {
      console.error('Error fetching components:', err);
      setComponents(prev => ({ ...prev, [batchId]: [] }));
    } finally {
      setLoadingComponents(prev => ({ ...prev, [batchId]: false }));
    }
  };

  // Handle drag start
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex === null || draggedIndex === index) return;

    // Reorder batches locally for visual feedback
    const newOrder = [...batches];
    const draggedBatch = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, draggedBatch);

    setBatches(newOrder);
    setDraggedIndex(index);
  };

  // Handle drop
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null) return;

    // Save the new order to backend
    await saveBatchOrder(batches);
    setDraggedIndex(null);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Save batch order to backend
  const saveBatchOrder = async (batchesArray) => {
    setReordering(true);
    try {
      const batchIds = batchesArray.map(b => b.id);
      await axios.post('/api/batches/reorder', { batchIds });
      // Refresh to get updated data
      await fetchBatches();
    } catch (err) {
      console.error('Error reordering batches:', err);
      setError('Failed to reorder batches');
      // Revert to original order
      await fetchBatches();
    } finally {
      setReordering(false);
    }
  };

  // Start editing quantities for a batch
  const startEditingQty = (batch) => {
    const initial = {};
    batch.batch_work_orders.forEach(wo => {
      initial[wo.id] = wo.batch_quantity;
    });
    setQtyEdits(initial);
    setEditingBatchQty(batch.id);
  };

  // Cancel editing
  const cancelEditingQty = () => {
    setEditingBatchQty(null);
    setQtyEdits({});
  };

  // Save quantity changes
  const saveQtyChanges = async (batch) => {
    // Build list of changes
    const updates = [];
    batch.batch_work_orders.forEach(wo => {
      const newQty = parseInt(qtyEdits[wo.id]);
      if (!isNaN(newQty) && newQty !== wo.batch_quantity) {
        updates.push({
          batch_work_order_id: wo.id,
          batch_quantity: newQty
        });
      }
    });

    if (updates.length === 0) {
      cancelEditingQty();
      return;
    }

    setSavingQty(true);
    try {
      await axios.patch(`/api/batches/${batch.id}/work-order-quantities`, { updates });
      setEditingBatchQty(null);
      setQtyEdits({});
      await fetchBatches();
    } catch (err) {
      console.error('Error saving quantity changes:', err);
      alert(err.response?.data?.error || 'Failed to save quantity changes');
    } finally {
      setSavingQty(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner size="medium" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchBatches}>Retry</button>
      </div>
    );
  }

  return (
    <div className="batch-manager">
      <div className="batch-manager-header">
        <div className="header-title-section">
          <h2>Production Batches</h2>
          <p className="reorder-hint">Use ↑↓ arrows to set production priority</p>
        </div>
        <div className="header-controls">
          <input
            type="text"
            placeholder="Search by WO number..."
            value={woSearchTerm}
            onChange={(e) => setWoSearchTerm(e.target.value)}
            className="wo-search-input"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="status-filter"
          >
            <option value="active">Active (not completed)</option>
            <option value="all">All Batches</option>
            <option value="draft">Draft</option>
            <option value="in_production">In Production</option>
            <option value="completed">Completed</option>
          </select>
          <button onClick={() => fetchBatches(true)} className="refresh-btn">
            Refresh from NetSuite
          </button>
        </div>
      </div>

      {batches.length === 0 ? (
        <div className="no-batches">
          {woSearchTerm.trim()
            ? `No batches found containing work order "${woSearchTerm}"`
            : 'No batches found. Create a batch from the Batching Worksheet tab.'}
        </div>
      ) : (
        <div className="batches-list">
          {batches.map((batch, index) => (
            <div
              key={batch.id}
              className={`batch-card ${draggedIndex === index ? 'dragging' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
            >
              <div
                className="batch-header"
                onClick={() => toggleBatchExpansion(batch.id)}
              >
                <div className="priority-controls" title="Drag to reorder">
                  <div className="drag-handle">
                    <span className="drag-icon">☰</span>
                  </div>
                </div>
                <div className="batch-info">
                  <span className="expand-icon">
                    {expandedBatches.has(batch.id) ? '▼' : '▶'}
                  </span>
                  <h3>{batch.batch_name}</h3>
                </div>
                <div className="batch-summary">
                  <div className="summary-item">
                    <span className="label">Fragrance:</span>
                    <span>{batch.fragrance_oil_name}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Units:</span>
                    <span>{batch.total_units}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Oil:</span>
                    <span>{batch.total_oil_lbs} lbs</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Wax:</span>
                    <span>{batch.total_wax_lbs} lbs</span>
                  </div>
                </div>
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(batch.status) }}
                >
                  {batch.status.replace('_', ' ')}
                </span>
                <div className="batch-header-actions" onClick={e => e.stopPropagation()}>
                  {batch.status === 'draft' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(batch.id, 'in_production');
                      }}
                      className="action-btn start-btn"
                      title="Start Production"
                    >
                      Start
                    </button>
                  )}
                  {batch.status === 'in_production' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(batch.id, 'in_production');
                      }}
                      className="action-btn complete-btn"
                      title="Mark Complete"
                    >
                      Complete
                    </button>
                  )}
                  <button
                    onClick={(e) => handleDeleteBatch(batch.id, batch.batch_name, batch.status, e)}
                    className="action-btn delete-btn"
                    title="Delete Batch"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {expandedBatches.has(batch.id) && (
                <div className="batch-details">
                  <div className="batch-metadata">
                    <div>Created: {formatDate(batch.created_at)}</div>
                    {batch.completed_at && (
                      <div>Completed: {formatDate(batch.completed_at)}</div>
                    )}
                  </div>

                  {/* Tab Navigation */}
                  <div className="batch-tabs">
                    <button
                      className={`tab-button ${!activeTab[batch.id] || activeTab[batch.id] === 'workorders' ? 'active' : ''}`}
                      onClick={() => setActiveTab({...activeTab, [batch.id]: 'workorders'})}
                    >
                      Work Orders ({batch.batch_work_orders.length})
                    </button>
                    <button
                      className={`tab-button ${activeTab[batch.id] === 'carts' ? 'active' : ''}`}
                      onClick={() => setActiveTab({...activeTab, [batch.id]: 'carts'})}
                    >
                      Carts
                    </button>
                    <button
                      className={`tab-button ${activeTab[batch.id] === 'blends' ? 'active' : ''}`}
                      onClick={() => setActiveTab({...activeTab, [batch.id]: 'blends'})}
                    >
                      Blends
                    </button>
                    <button
                      className={`tab-button ${activeTab[batch.id] === 'components' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab({...activeTab, [batch.id]: 'components'});
                        fetchComponents(batch.id);
                      }}
                    >
                      Components
                    </button>
                  </div>

                  {/* Tab Content */}
                  {(!activeTab[batch.id] || activeTab[batch.id] === 'workorders') ? (
                    <div className="work-orders-table">
                      {batch.status !== 'completed' && (
                        <div className="wo-table-actions">
                          {editingBatchQty === batch.id ? (
                            <>
                              <button
                                className="action-btn start-btn"
                                onClick={() => saveQtyChanges(batch)}
                                disabled={savingQty}
                              >
                                {savingQty ? 'Saving...' : 'Save Changes'}
                              </button>
                              <button
                                className="action-btn"
                                onClick={cancelEditingQty}
                                disabled={savingQty}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              className="action-btn"
                              onClick={() => startEditingQty(batch)}
                            >
                              Edit Quantities
                            </button>
                          )}
                        </div>
                      )}
                      <table>
                      <thead>
                        <tr>
                          <th>WO Number</th>
                          <th>Customer</th>
                          <th>Item</th>
                          <th>Original Qty</th>
                          <th>Batch Qty</th>
                          <th>Remaining Qty</th>
                          <th>Units</th>
                          <th>Oil (lbs)</th>
                          <th>Wax (lbs)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batch.batch_work_orders.map(wo => {
                          const isEditing = editingBatchQty === batch.id;
                          const editQty = isEditing ? (qtyEdits[wo.id] ?? wo.batch_quantity) : wo.batch_quantity;
                          const displayUnits = editQty * (wo.case_qty || 1);

                          return (
                            <tr key={wo.id} className={isEditing && parseInt(editQty) !== wo.batch_quantity ? 'qty-changed' : ''}>
                              <td>{wo.work_order_number}</td>
                              <td>{wo.customer_name}</td>
                              <td>{wo.item_description}</td>
                              <td>{wo.original_quantity}</td>
                              <td>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    min="1"
                                    value={editQty}
                                    onChange={(e) => setQtyEdits(prev => ({
                                      ...prev,
                                      [wo.id]: e.target.value
                                    }))}
                                    className="qty-edit-input"
                                  />
                                ) : (
                                  wo.batch_quantity
                                )}
                              </td>
                              <td>
                                {wo.quantity_remaining !== undefined
                                  ? wo.quantity_remaining
                                  : (wo.original_quantity - wo.batch_quantity)}
                              </td>
                              <td>{displayUnits}</td>
                              <td>{wo.oil_needed_lbs}</td>
                              <td>{wo.wax_needed_lbs}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      </table>
                    </div>
                  ) : activeTab[batch.id] === 'carts' ? (
                    <CartList batch={batch} />
                  ) : activeTab[batch.id] === 'blends' ? (
                    <BlendManager batch={batch} />
                  ) : activeTab[batch.id] === 'components' ? (
                    <div className="components-table">
                      {loadingComponents[batch.id] ? (
                        <LoadingSpinner size="small" />
                      ) : components[batch.id] && components[batch.id].length > 0 ? (
                        <table>
                          <thead>
                            <tr>
                              <th>SKU</th>
                              <th>Component Name</th>
                              <th>Total Needed</th>
                              <th>On Hand</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {components[batch.id].map((comp, idx) => {
                              const isSufficient = comp.onHand >= comp.totalQty;
                              return (
                                <tr key={idx} className={isSufficient ? '' : 'insufficient'}>
                                  <td>{comp.sku}</td>
                                  <td>{comp.name}</td>
                                  <td>{comp.totalQty}</td>
                                  <td>{comp.onHand}</td>
                                  <td className="status-cell">
                                    {isSufficient ? (
                                      <span className="status-sufficient">✓ Sufficient</span>
                                    ) : (
                                      <span className="status-insufficient">⚠️ Insufficient</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      ) : (
                        <div className="no-components">No components found for this batch</div>
                      )}
                    </div>
                  ) : null}

                  <div className="batch-actions">
                    <button
                      onClick={() => exportBatchToCSV(batch)}
                      className="action-btn export-btn"
                    >
                      Export CSV
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BatchManager;